import { BREAKPOINTS, PRODUCT_CARD, SELECTORS, SWIPER } from '../utils/constants';
import addToCart from '../lib/addToCart';
import renderMinicart from '../lib/renderMinicart';
import updateProductGallery from '../utils/product/updateProductGallery';
import { initSwiper } from './swiper';

const ARIA_CURRENT = 'aria-current';
const CARD_GALLERY_SELECTOR = '[data-card-gallery]';
const SLIDE_IMAGE_TEMPLATE_SELECTOR = 'template[data-slide-image]';

const setLoadingState = (buttonEl, isLoading) => {
  const spinnerEl = buttonEl.querySelector(SELECTORS.LOADING_SPINNER);
  buttonEl.classList.toggle('pointer-events-none', isLoading);
  buttonEl.classList.toggle('opacity-50', isLoading);
  if (spinnerEl) spinnerEl.classList.toggle('hidden', !isLoading);
};

const initCardAtc = atcButtonEl => {
  if (!atcButtonEl) return;
  let isLoading = false;

  atcButtonEl.addEventListener('click', async () => {
    const variantId = Number(atcButtonEl.dataset.cardAtc);
    if (isLoading || !variantId) return;

    isLoading = true;
    setLoadingState(atcButtonEl, true);
    try {
      const result = await addToCart(variantId, 1);
      // addToCart swallows network errors (undefined) and resolves error
      // responses like 422 as {status} — only open the minicart on success
      if (!result || result.status) {
        console.error('Add to cart failed:', result);
        return;
      }
      await renderMinicart();
      window.theme.minicart?.open();
    } catch (error) {
      console.error('Error adding variant to cart:', error);
    } finally {
      isLoading = false;
      setLoadingState(atcButtonEl, false);
    }
  });
};

// updateProductGallery matches slides by variant id (admin attachment) and SKU (alt/filename tag)
const getSwatchVariant = swatchEl => ({
  id: swatchEl.dataset.variantId,
  sku: swatchEl.dataset.variantSku
});

const getGalleryLimit = galleryEl =>
  Number(galleryEl.dataset.galleryLimit) || PRODUCT_CARD.GALLERY_LIMIT;

// Card slides ship their image inside a <template> (product-card-slide
// is_deferred) because the browser fetches lazy images inside the clipped
// slides; hydrating moves the image into the slide
const hydrateSlide = slideEl => {
  const templateEl = slideEl?.querySelector(SLIDE_IMAGE_TEMPLATE_SELECTOR);
  if (templateEl) templateEl.replaceWith(templateEl.content);
};

// The active slide after every rebuild (or the slide a deferred move is
// about to land on); its neighbours once a swipe starts
const hydrateActiveSlides = (galleryEl, { index, isIncludingNeighbours = false } = {}) => {
  const { swiper } = galleryEl;
  if (!swiper) {
    hydrateSlide(galleryEl.querySelector(SWIPER.SLIDE_CLASS));
    return;
  }
  const { slides } = swiper;
  const activeIndex = Math.min(index ?? swiper.activeIndex, slides.length - 1);
  const indexes = isIncludingNeighbours
    ? [activeIndex - 1, activeIndex, activeIndex + 1]
    : [activeIndex];
  indexes.forEach(slideIndex => hydrateSlide(slides[slideIndex]));
};

// A hover peek keeps the slide the user is on (every colour carries the
// same shot sequence), clamped to the new set; a selection starts from the
// colour's first image
const rebuildCardGallery = (cardEl, galleryEl, variant, options = {}) => {
  const { isKeepingPosition = false } = options;
  const activeIndex = isKeepingPosition ? galleryEl.swiper?.activeIndex ?? 0 : 0;
  updateProductGallery(variant, cardEl, { limit: getGalleryLimit(galleryEl), activeIndex });
  // slideToLoop defers a frame, like the updater's own call, so a reset right
  // after a hover peek still lands last
  if (!isKeepingPosition) galleryEl.swiper?.slideToLoop(0, 0);
  hydrateActiveSlides(galleryEl, { index: activeIndex });
};

// Collection grid card: the carousel is rebuilt to the variant's first slides
// (product-card-gallery.liquid parks the other swatch variants' slides in a
// pool). A click selects the colour at every width; the card link then
// carries the variant to the PDP
const createGalleryPreview = (cardEl, galleryEl) => {
  let selectedVariant = { id: galleryEl.dataset.variantId, sku: galleryEl.dataset.variantSku };
  const peek = variant =>
    rebuildCardGallery(cardEl, galleryEl, variant, { isKeepingPosition: true });
  return {
    isClickSelect: true,
    show: swatchEl => peek(getSwatchVariant(swatchEl)),
    restore: () => peek(selectedVariant),
    select: swatchEl => {
      selectedVariant = getSwatchVariant(swatchEl);
      rebuildCardGallery(cardEl, galleryEl, selectedVariant);
    }
  };
};

// Every other card: the single image's src is swapped for the swatch's
// variant image; a desktop click follows the swatch link to the PDP
const createImagePreview = cardEl => {
  const imageEl = cardEl.querySelector('[data-card-image] img');
  const secondaryImageEl = cardEl.querySelector('[data-card-image-secondary]');
  let defaultSrc = imageEl?.src;
  let defaultSrcset = imageEl?.getAttribute('srcset');
  return {
    isClickSelect: false,
    show: swatchEl => {
      const { swatchImage } = swatchEl.dataset;
      if (!imageEl || !swatchImage) return;
      imageEl.src = swatchImage;
      imageEl.removeAttribute('srcset');
      // A variant preview replaces the generic second-image hover swap
      if (secondaryImageEl) secondaryImageEl.hidden = true;
    },
    restore: () => {
      if (imageEl && defaultSrc) {
        imageEl.src = defaultSrc;
        if (defaultSrcset) imageEl.setAttribute('srcset', defaultSrcset);
      }
      if (secondaryImageEl) secondaryImageEl.hidden = false;
    },
    select: swatchEl => {
      const { swatchImage } = swatchEl.dataset;
      if (!swatchImage) return;
      defaultSrc = swatchImage;
      defaultSrcset = null;
    }
  };
};

const createPreview = cardEl => {
  const galleryEl = cardEl.querySelector(CARD_GALLERY_SELECTOR);
  return galleryEl ? createGalleryPreview(cardEl, galleryEl) : createImagePreview(cardEl);
};

const setAtcVariant = (atcButtonEl, swatchEl, atcText) => {
  const { variantId, variantAvailable } = swatchEl.dataset;
  if (!atcButtonEl || !variantId) return;
  const isAvailable = variantAvailable === 'true';
  const atcLabelEl = atcButtonEl.querySelector('[data-atc-label]');
  atcButtonEl.dataset.cardAtc = variantId;
  atcButtonEl.disabled = !isAvailable;
  if (atcLabelEl) {
    atcLabelEl.textContent = isAvailable ? atcText : window.theme.strings.soldOut;
  }
};

const initCardSwatches = (cardEl, atcButtonEl, desktopMediaQuery) => {
  const swatchEls = cardEl.querySelectorAll('[data-card-swatch]');
  if (!swatchEls.length) return;

  const preview = createPreview(cardEl);
  const linkEls = cardEl.querySelectorAll('[data-card-link]');
  const priceEl = cardEl.querySelector('[data-card-price]');
  const statusEl = cardEl.querySelector('[data-card-status]');

  const defaultState = {
    priceText: priceEl?.textContent.trim(),
    atcText: atcButtonEl?.querySelector('[data-atc-label]')?.textContent.trim(),
    currentSwatchEl: cardEl.querySelector('[data-card-swatch][aria-current="true"]')
  };

  const showVariant = swatchEl => {
    swatchEls.forEach(el => el.setAttribute(ARIA_CURRENT, 'false'));
    swatchEl.setAttribute(ARIA_CURRENT, 'true');
    preview.show(swatchEl);
    const { variantPrice } = swatchEl.dataset;
    if (priceEl && variantPrice) priceEl.textContent = variantPrice;
  };

  // Desktop hover is a temporary preview — fall back to the card's own
  // variant once the pointer leaves the swatch
  const clearPreview = () => {
    preview.restore();
    if (priceEl && defaultState.priceText) priceEl.textContent = defaultState.priceText;
    swatchEls.forEach(el => el.setAttribute(ARIA_CURRENT, 'false'));
    defaultState.currentSwatchEl?.setAttribute(ARIA_CURRENT, 'true');
  };

  // A tap (and on the carousel card any click) is a persistent selection —
  // card links and quick add follow it, and it becomes the state
  // clearPreview restores to
  const selectSwatch = swatchEl => {
    showVariant(swatchEl);
    preview.select(swatchEl);
    const { variantPrice } = swatchEl.dataset;
    if (variantPrice) defaultState.priceText = variantPrice;
    defaultState.currentSwatchEl = swatchEl;
    setAtcVariant(atcButtonEl, swatchEl, defaultState.atcText);
    linkEls.forEach(linkEl => {
      linkEl.href = swatchEl.href;
    });
    // The focus preview already marks the swatch current, so announce the
    // selection; clearing first makes a repeat of the same colour re-announce
    if (statusEl) {
      statusEl.textContent = '';
      requestAnimationFrame(() => {
        statusEl.textContent = swatchEl.getAttribute('aria-label');
      });
    }
  };

  swatchEls.forEach(swatchEl => {
    swatchEl.addEventListener('mouseenter', () => {
      if (desktopMediaQuery.matches) showVariant(swatchEl);
    });
    swatchEl.addEventListener('mouseleave', () => {
      if (desktopMediaQuery.matches) clearPreview();
    });
    swatchEl.addEventListener('focus', () => {
      if (desktopMediaQuery.matches) showVariant(swatchEl);
    });
    swatchEl.addEventListener('blur', () => {
      if (desktopMediaQuery.matches) clearPreview();
    });
    swatchEl.addEventListener('click', event => {
      if (!desktopMediaQuery.matches || preview.isClickSelect) {
        event.preventDefault();
        selectSwatch(swatchEl);
      }
    });
  });
};

// Collection grid card carousel: the server renders slides in media position
// order, so sort them to the media-tags order and cap them the way the PDP
// gallery does on load, then start the swiper for cards injected after the
// page init (filters, load more) — the initial grid is handled by initSwiper
const initCardGallery = cardEl => {
  const galleryEl = cardEl.querySelector(CARD_GALLERY_SELECTOR);
  if (!galleryEl) return;
  const { variantId, variantSku } = galleryEl.dataset;
  const variant = { id: variantId, sku: variantSku };
  updateProductGallery(variant, cardEl, { limit: getGalleryLimit(galleryEl) });
  if (!galleryEl.swiper) initSwiper(galleryEl);
  hydrateActiveSlides(galleryEl);

  const { swiper } = galleryEl;
  if (!swiper) return;
  const hydrateNeighbours = () => hydrateActiveSlides(galleryEl, { isIncludingNeighbours: true });
  swiper.on('touchStart', hydrateNeighbours);
  swiper.on('slideChange', hydrateNeighbours);
  // Desktop: have the next image ready before the pointer starts a drag
  cardEl.addEventListener('mouseenter', hydrateNeighbours);
};

const initProductCards = () => {
  const cardEls = document.querySelectorAll('[data-product-card]');
  if (!cardEls.length) return;

  const desktopMediaQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.DESKTOP}px)`);

  cardEls.forEach(cardEl => {
    if (cardEl.dataset.cardInit) return;
    cardEl.dataset.cardInit = 'true';

    const atcButtonEl = cardEl.querySelector('[data-card-atc]');
    initCardAtc(atcButtonEl);
    initCardGallery(cardEl);
    initCardSwatches(cardEl, atcButtonEl, desktopMediaQuery);
  });
};

// Public hook for scripts outside the theme bundle: Bloomreach injects cards
// fetched from ?view=card after page load and calls this to bind them.
// initProductCards skips cards it has already bound, so repeat calls are safe.
const initProductCardsApi = () => {
  if (!window.theme || window.theme.productCards) return;
  window.theme.productCards = { init: initProductCards };
};

export { initProductCardsApi };
export default initProductCards;
